import { colors } from "src/constants";
import { GameChannel } from "src/components/TableColumns/ChannelLogo";
import { CustomerLogType } from "src/types";
import { CustomerLog } from "src/types/api";
import { decimal } from "src/utils";
import { addCommas } from "src/utils/number/number";

export function AmountColor({ value }: { value: number }) {
  return (
    <div
      style={{
        textAlign: "right",
        color: value > 0 ? colors.green7 : value < 0 ? colors.red7 : colors.cloud9,
      }}
    >
      {value !== null ? addCommas(value > 0 ? `+${decimal(value)}` : decimal(value)) : "-"}
    </div>
  );
}

export const gameProviderName: Record<string, string> = {
  "JOKER_GAME": "Joker",
  "PG_GAME": "PG Soft",
  "EVO_GAME": "EVO Play",
  "EVOLUTION": "Evolution",
  "SEXY": "Sexy",
  "SA": "SA",
  "BG": "Big Gaming",
  "DREAM_GAMING": "Dream Gaming",
  "JILI_GAME": "Jili",
  "PNG_GAME": "Play N Go",
  "PRAGMATIC_SLOT_GAME": "Pragmatic Slot",
  "PRAGMATIC_LIVE_GAME": "Pragmatic Live",
  "AMBSLOT2_GAME":"AMB Slot",
  "RELAX_GAME": "Relax",
  "MICRO_GAME": "Micro",
  "YGGDRASIL_GAME": "Yggdrasil",
  "CQ9_GAME": "CQ9",
  "CQ9_LIVE": "CQ9 Live",
  "CG_GAME": "CreativeGaming",
  "NETENT2_GAME": "Netent2",
  "REDTIGER_GAME": "Redtiger",
  "FUNKY_GAME":"Funky",
  "I8_GAME": "I8",
  "MANNA_GAME": "Manna",
  "SPINIX_GAME": "Spinix",
  "TURBO_GAMES": "Turbo Games",
  "FC_GAME": "FC",
  "FASTSPIN_GAME": "FASTSPIN",
  "SPADE_GAME": "SPADE",
  "HACK_SAW": "HACK_SAW",
  "SPRIBE_GAME": "SPRIBE",
  "NLC_GAME": "NLC",
  "BTG_GAME": "BTG",
  "DRAGOONSOFT_GAME": "DRAGOONSOFT",
  "PLAY8_GAME": "PLAY8",
  "JDB_GAME": "JDB",
  "GTF_GAME": "GTF",
  "OCTOPLAY_GAME": "Octoplay",
  "RTGSLOT_GAME": "Real Time Gaming",
  "AMEBA_GAME": "Ameba",
  "ASKMEBET_GAME": "Ask Me Bet",
  "AMBPOKER_GAME": "AMB Poker",
  "ASKMEPLAY_GAME": "Ask Me Play",
  "ACE333_GAME": "Ace333",
  "AMB_5G_GAME": "AMB 5G",
  "AMB_918KISS_GAME": "AMB 918Kiss",
  "SEXY_SLOT_GAME": "Sexy Slot",
  "NINJA_GAME": "Ninja",
  "MPY": "MPY",
  "SBO_SPORTBOOK": "SBO_SPORTBOOK",
  "NEXTSPIN_GAME": "NEXTSPIN",
  "AMBGAMING_GAME": "AMB GAMING",
  "LIVE22_GAME": "LIVE22",
  "ROYAL_GAME": "ROYAL",
  "RICH88_GAME": "RICH88",
  "YGR_GAME": "YGR",
  "BOOONGO_GAME": "BOOONGO",
  "KAGAME_GAME": "KAGAME",
  "AMB_759GAMING_GAME": "AMB 759 GAMING",
  "WAZDAN_GAME": "WAZDAN",
  "HABANERO_GAME": "HABANERO",
  "SIMPLEPLAY_GAME": "SIMPLE PLAY",
  "BOOMING_GAME": "BOOMING",
  "FUNTA_GAME": "FUNTA",
  "NINESGAME_GAME": "NINES GAME",
  "GIOCO_GAME": "GIOCO",
  "DRAGONGAMING_GAME": "DRAGON GAMING",
  "GOLDY_GAME": "GOLDY",
  "SABA": "SABA",
  "BIGPOT_GAME": "Big Pot",
  "EXPANSE_GAME": "Expanse",
  "HOTDOG_GAME": "Hot Dog",
  "SLOTXO_GAME": "Slotxo",
  "UFABET_GAME": "Ufabet+",
};

// Providers not listed here are slot providers. The SLOT filter sends every gameProviderName key not
// listed here, so a new provider must be added to gameProviderName to show up under SLOT.
export const gameProviderChannel: Record<string, GameChannel> = {
  "MPY": "SPORT",
  "SBO_SPORTBOOK": "SPORT",
  "SABA": "SPORT",
  "UFABET_GAME": "SPORT",
  // Live-casino brands of PIGBET (game brand list, party PIGBET).
  "SEXY": "BET",
  "SA": "BET",
  "BG": "BET",
  "DREAM_GAMING": "BET",
  "EVOLUTION": "BET",
  "PRAGMATIC_LIVE_GAME": "BET",
  "CQ9_LIVE": "BET",
};

// Game rows written before transaction_log had game_provider were all PG; the game-stats tab assumes the same.
export const LEGACY_GAME_PROVIDER = "PG_GAME";

export function getRowGameProvider(record: CustomerLog): string | undefined {
  if (record.gameProvider) return record.gameProvider;
  return record.transactionType === CustomerLogType.GAME ? LEGACY_GAME_PROVIDER : undefined;
}

export function getGameProviderName(gameProvider?: string | null): string {
  if (!gameProvider) return "";
  return (gameProviderName[gameProvider] || gameProvider).toUpperCase();
}

export function getGameChannel(gameProvider?: string | null): GameChannel | undefined {
  if (!gameProvider) return undefined;
  return gameProviderChannel[gameProvider] || "SLOT";
}

export const ALL_CHANNELS = "ALL";

// Wallet only sends betAt for sport bills whose bet and payout are separate rows.
export function isSportBill(record: CustomerLog): boolean {
  return Boolean(record.betAt);
}

export function getProvidersOfChannel(channel: GameChannel | typeof ALL_CHANNELS): string[] {
  return Object.keys(gameProviderName).filter(
    (provider) => channel === ALL_CHANNELS || getGameChannel(provider) === channel
  );
}

export function getGameProvidersFilter(channel: GameChannel | typeof ALL_CHANNELS, gameProvider?: string) {
  if (gameProvider) return [gameProvider];
  return channel === ALL_CHANNELS ? undefined : getProvidersOfChannel(channel);
}
